import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('memorialService');
// Memorial Service Detail Interfaces
export interface ChapelDetails {
  liveStreamingService?: boolean;
  viewingBeforeService?: boolean;
  receptionAfter?: boolean;
  specificRequests?: string;
  musicPreferences?: string;
}

export interface ChurchDetails {
  typeOfMusic?: string;
  specificClergy?: string;
  communion?: boolean;
  religiousReadings?: string;
  denominationRequirements?: string;
}

export interface CemeteryDetails {
  outdoorTent?: boolean;
  chairsProvided?: boolean;
  typeOfCeremony?: string;
  weatherContingency?: string;
  gravesideMusic?: boolean;
}

export interface ResidenceDetails {
  homeLocation?: string;
  maxGuestCount?: string;
  cateringNeeds?: boolean;
  parkingArrangements?: string;
  soundSystemNeeded?: boolean;
}

export interface OutdoorDetails {
  specificLocation?: string;
  permitRequired?: boolean;
  weatherBackupPlan?: string;
  seatingArrangements?: string;
  accessibilityConsiderations?: string;
}

export interface OtherDetails {
  customDescription?: string;
  specialVenue?: string;
  uniqueRequirements?: string;
  logisticalNeeds?: string;
}

export type ServiceDetails = ChapelDetails | ChurchDetails | CemeteryDetails | ResidenceDetails | OutdoorDetails | OtherDetails | null;

export interface MemorialServiceInfoRec {
  id: string;
  userId: string;
  serviceType?: string;
  specificInstructions?: string;
  preferredLocation?: string;
  contactName?: string;
  contactPhone?: string;
  estimatedCost?: number;
  notes?: string;
  serviceDetails?: ServiceDetails;
  username?: string;
  password?: string;
  accountNumber?: string;
  isLocked?: boolean;
}

class MemorialServiceInfoService {
  async getAllMemorialServiceInfo(): Promise<MemorialServiceInfoRec[]> {
    const response = await http.get('/api/memorial-service');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch memorial service information');
    }

    return response.data.data || [];
  }

  async getMemorialServiceInfoById(id: string): Promise<MemorialServiceInfoRec | null> {
    try {
      const response = await http.get(`/api/memorial-service/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch memorial service information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching memorial service information by ID:', error);
      return null;
    }
  }

  async createMemorialServiceInfo(memorialServiceData: Partial<MemorialServiceInfoRec>): Promise<MemorialServiceInfoRec> {
    const response = await http.post('/api/memorial-service', memorialServiceData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save memorial service information');
    }

    return response.data.data;
  }

  async updateMemorialServiceInfo(id: string, memorialServiceData: Partial<MemorialServiceInfoRec>): Promise<MemorialServiceInfoRec> {
    const response = await http.patch(`/api/memorial-service/${id}`, memorialServiceData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update memorial service information');
    }

    return response.data.data;
  }

  async deleteMemorialServiceInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/memorial-service/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete memorial service information');
    }
  }

  async lockMemorialServiceInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for memorial service ID:', id);
    const response = await http.post(`/api/memorial-service/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock memorial service information');
    }
  }

  async unlockMemorialServiceInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for memorial service ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/memorial-service/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock memorial service information');
    }
  }

  async getMemorialServiceAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/memorial-service/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch memorial service access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching memorial service access grants:', error);
      return [];
    }
  }

  async deleteMemorialServiceAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/memorial-service/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const memorialServiceInfoService = new MemorialServiceInfoService();